import { useState } from 'react';
import { useFontScale } from './useFontScale';
import { FontSize } from './useFontSizeSettings';
import { useMicrophoneSettings } from './useMicrophoneSettings';

interface MicrophoneDevice {
  deviceId: string;
  label: string;
  displayName: string;
  type: string;
  description: string;
}

interface UseGeneralizedSettingsReturn {
  settingsAnchor: HTMLElement | null;
  handleSettingsClick: (event: React.MouseEvent<HTMLElement>) => void;
  handleSettingsClose: () => void;

  microphoneSubmenuAnchor: HTMLElement | null;
  handleMicrophoneSubmenuOpen: (event: React.MouseEvent<HTMLElement>) => void;
  handleMicrophoneSubmenuClose: () => void;

  fontSizeSubmenuAnchor: HTMLElement | null;
  handleFontSizeSubmenuOpen: (event: React.MouseEvent<HTMLElement>) => void;
  handleFontSizeSubmenuClose: () => void;

  availableMicrophones: MicrophoneDevice[];
  selectedMicrophoneId: string;
  microphoneError: string | null;
  isLoadingMicrophones: boolean;
  handleMicrophoneSelect: (microphoneId: string) => void;
  getSelectedDeviceId: () => string;
  refreshMicrophones: () => Promise<void>;

  selectedFontSize: FontSize;
  handleFontSizeSelect: (fontSize: FontSize) => void;

  handleLogout?: () => void;
}

export const useGeneralizedSettings = (
  onLogout?: () => void
): UseGeneralizedSettingsReturn => {
  const [settingsAnchor, setSettingsAnchor] = useState<HTMLElement | null>(
    null
  );

  const [microphoneSubmenuAnchor, setMicrophoneSubmenuAnchor] =
    useState<HTMLElement | null>(null);

  const [fontSizeSubmenuAnchor, setFontSizeSubmenuAnchor] =
    useState<HTMLElement | null>(null);

  const {
    availableMicrophones,
    selectedMicrophoneId,
    microphoneError,
    isLoadingMicrophones,
    handleMicrophoneSelect,
    getSelectedDeviceId,
    refreshMicrophones,
  } = useMicrophoneSettings();

  const { selectedFontSize, handleFontSizeSelect } = useFontScale();

  const handleSettingsClick = (event: React.MouseEvent<HTMLElement>) => {
    setSettingsAnchor(event.currentTarget);
  };

  const handleSettingsClose = () => {
    setSettingsAnchor(null);
    setMicrophoneSubmenuAnchor(null);
    setFontSizeSubmenuAnchor(null);
  };

  const handleMicrophoneSubmenuOpen = (
    event: React.MouseEvent<HTMLElement>
  ) => {
    event.stopPropagation();
    setMicrophoneSubmenuAnchor(event.currentTarget);
  };

  const handleMicrophoneSubmenuClose = () => {
    setMicrophoneSubmenuAnchor(null);
  };

  const handleFontSizeSubmenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setFontSizeSubmenuAnchor(event.currentTarget);
  };

  const handleFontSizeSubmenuClose = () => {
    setFontSizeSubmenuAnchor(null);
  };

  const handleLogout = () => {
    handleSettingsClose();
    onLogout?.();
  };

  return {
    settingsAnchor,
    handleSettingsClick,
    handleSettingsClose,

    microphoneSubmenuAnchor,
    handleMicrophoneSubmenuOpen,
    handleMicrophoneSubmenuClose,

    fontSizeSubmenuAnchor,
    handleFontSizeSubmenuOpen,
    handleFontSizeSubmenuClose,

    availableMicrophones,
    selectedMicrophoneId,
    microphoneError,
    isLoadingMicrophones,
    handleMicrophoneSelect,
    getSelectedDeviceId,
    refreshMicrophones,

    selectedFontSize,
    handleFontSizeSelect,

    handleLogout,
  };
};
